/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { createEnum, custom, DataMapper, EnumSet, WithOptimisticLock } from '@gh/core-data';

import { MasterZoneListItemDto } from '../../master-zones/master-zone-list-item.dto';
import { OfficeListItemDto } from './office-list-item.dto';
import { OfficeMeasurementDefault } from './office-measurement-default.enum';
import { OfficeMeasurement } from './office-measurement.enum';
import { OfficeStatus } from './office-status.enum';
import { OfficeType } from './office-type.enum';
import { UnpaidInvoiceInfo } from '../../common';

export const Month = createEnum('MONTH', {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
});
export type Month = EnumSet<typeof Month>;

export type OfficeImportTemplateDto = {
  id: ID;
  name: string;
  checked: boolean;
  dependIds: ID[];
  parentId?: ID;
};

export type OfficeDetailsDto = WithOptimisticLock<{
  id: ID;
  name: string;
  status: OfficeStatus;
  statusValue: OfficeStatus;
  type: OfficeType;
  masterZone: MasterZoneListItemDto;
  typeOfBusiness: string;
  formedDate: Date;
  companyName: string;
  displayName: string;
  systemName: string;
  taxId: string;
  tradingAs: string;
  abn: string;
  acn: string;
  gst: string;
  publishToWeb: boolean;
  isPublishToWebAvailable: boolean;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postCode: string;
  email: string;
  phoneOffice: string;
  fax: string;
  phoneMobile: string;
  measurement: OfficeMeasurement;
  templates: OfficeImportTemplateDto[];
  timeZone: string;
  measurementDefault: OfficeMeasurementDefault;
  currentFinYear: number;
  lastMonthFinYear: Month;
  conversionDate: Date;
  licenseNo?: string;
  sourceOffice?: OfficeListItemDto;
  planName: string;
  monthlyPrice: number;
  customMonthlyPrice: number;
  billing: {
    after36Months: number;
    assignmentFee: number;
    contractSignedDate: Date;
    currentAgreement: Date;
    depositDate: Date;
    franchiseFee: number;
    initialFranchiseFee: number;
    officeClosed: Date;
    onSellDate: Date;
    originalCommencement: Date;
    renewalDate: Date;
    saleAmount: number;
    taxRate: number;
    term: number;
    within18Months: number;
    within24Months: number;
    within36Months: number;
  };
  unpaidInvoices?: UnpaidInvoiceInfo[];
  includeInactive?: boolean;
  followProductFee?: boolean;
  officeFee?: number;
  customFeeEditable: boolean;
}>;

export type OfficeSettingsRequestDto = Hash<any>;
export type OfficeSettingsDto = Hash<any>;

export type OfficeVariableSet<T> = {
  [P in keyof T]: OfficeVariable<T[P]>;
};

export type OfficeVariable<T> = {
  name: string;
  mapper: DataMapper<T, any>;
};

const SysVarBooleanVariableMapper = custom<boolean>({
  serialize: (value: boolean) => value.toString(),
  deserialize: (value) => value === 'true',
});

export const booleanVariable = (name: string): OfficeVariable<boolean> => ({
  name,
  mapper: SysVarBooleanVariableMapper,
});
